/**
 * Sentry Configuration for Frontend Error Tracking
 *
 * Captures errors, tracks performance, and sends to Sentry.
 * PII is automatically scrubbed (no emails, tokens, etc.).
 *
 * Environment-aware:
 * - Production: Replays, User Feedback, full tracing
 * - Staging: User Feedback (testing), reduced tracing, no replays
 * - Development: disabled (no DSN)
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry SDK
 *
 * Call this ONCE in main.tsx before React renders.
 */
/**
 * A valid Sentry DSN looks like https://<publicKey>@<host>/<projectId>.
 * Reject empty values and unsubstituted placeholders (e.g.
 * "YOUR_FRONTEND_DSN_HERE") so we don't spam the ingest endpoint with
 * 400s on environments where the DSN was never configured.
 */
function isValidDsn(dsn: string | undefined): dsn is string {
  if (!dsn) return false;
  try {
    const url = new URL(dsn);
    if (!url.protocol.startsWith('http')) return false;
    if (!url.username) return false; // public key segment missing
    if (/your[_-]|[_-]here|placeholder|changeme|xxxx/i.test(dsn)) return false;
    return true;
  } catch {
    return false;
  }
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!isValidDsn(dsn)) {
    console.log('[Sentry] No valid DSN configured - error tracking disabled');
    return;
  }

  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
  // Raw version identifier WITHOUT 'goki-frontend@' prefix — Sentry trennt
  // Releases bereits per Project. Mit reinem String matched die CI-seitige
  // Release-Erzeugung trivial. Auch 'unknown' (Default vom Dockerfile-ARG)
  // wird gefiltert, weil das kein sinnvoller Release-Identifier ist.
  const candidate =
    import.meta.env.VITE_GIT_TAG ??
    import.meta.env.VITE_APP_VERSION ??
    import.meta.env.VITE_GIT_SHA;
  const release = candidate && candidate !== 'unknown' ? candidate : undefined;

  const isProduction = environment === 'production';
  const isStaging = environment === 'staging';

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(isProduction
        ? [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })]
        : []),
      ...((isProduction || isStaging)
        ? [Sentry.feedbackIntegration({
            colorScheme: 'system',
            isNameRequired: false,
            isEmailRequired: false,
            showBranding: false,
            triggerLabel: isProduction ? 'Feedback' : 'Bug melden (Staging)',
            formTitle: 'Feedback senden',
            submitButtonLabel: 'Absenden',
            cancelButtonLabel: 'Abbrechen',
            nameLabel: 'Name',
            namePlaceholder: 'Dein Name',
            emailLabel: 'E-Mail',
            emailPlaceholder: 'deine@email.de',
            messageLabel: 'Was ist passiert?',
            messagePlaceholder: 'Beschreibe das Problem oder teile dein Feedback...',
            successMessageText: 'Danke für dein Feedback!',
          })]
        : []),
    ],

    // Performance: lower rate for staging
    tracesSampleRate: isProduction ? 0.1 : 0.02,

    // Replays: only production
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,

    // Privacy & Security
    beforeSend(event) {
      return scrubPII(event);
    },

    // Error Filtering
    ignoreErrors: [
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'NetworkError',
      'Failed to fetch',
    ],

    debug: false,
  });

  console.log(`[Sentry] Initialized for ${environment}${release ? ` (${release})` : ''}`);
}

/**
 * Scrub PII from Sentry events (GDPR compliance)
 */
function scrubPII(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.request) {
    if (event.request.cookies) {
      event.request.cookies = {};
    }
    if (event.request.query_string) {
      event.request.query_string = '[REDACTED]';
    }
    if (event.request.data) {
      const data = event.request.data as Record<string, unknown>;
      const sensitiveKeys = ['password', 'token', 'api_key', 'secret', 'credit_card', 'email'];
      Object.keys(data).forEach((key) => {
        if (sensitiveKeys.some((s) => key.toLowerCase().includes(s))) {
          data[key] = '[REDACTED]';
        }
      });
    }
  }

  if (event.user) {
    event.user = {
      id: event.user.id,
      workspace_id: (event.user as { workspace_id?: string }).workspace_id,
    };
  }

  return event;
}

/**
 * Set user context (call after login)
 */
export function setUserContext(userId: string, workspaceId: string): void {
  Sentry.setUser({ id: userId, workspace_id: workspaceId });
}

/**
 * Clear user context (call after logout)
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}

/**
 * Capture a custom message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
  Sentry.captureMessage(message, level);
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error): void {
  Sentry.captureException(error);
}
